import { useOrganizationContext } from '@/contexts/organization-context';
import SettingsTabTitle from '../settings-tab-title';
import { gql } from '@/__generated__';
import ReligiousCalendars from './religious-calendars';
import CustomerReviewSection from './customer-review';
import SocialMediaChannels from './social-media-channels';
import { Button } from '@/components/shadcn/button';
import { ApolloError, useMutation } from '@apollo/client';
import { Suspense, useState } from 'react';
import { Spinner } from '@/components/basic/spinner';
import { useToast } from '@/components/shadcn/use-toast';
import {
  OrgEventsMetadata,
  OrgEventsMetadataSignificantDateSource,
  Organization,
  PostReview,
  SchedulingVariantV1,
  SchedulingVariantV1Channel,
} from '@/__generated__/graphql';
import { toChannelInput } from '../integrations-tab/disconnect-button';

const MutationOrgEventMetadata = gql(`
  mutation MutationOrgEventMetadata($input: OrgEventsMetadataUpdateInput!) {
    orgEventsMetadata {
      update(input: $input) {
        ... on OrgEventsMetadataUpdateSuccess {
          result {
            id
          }
        }
      }
    }
  }
`);

const MutationOrganization = gql(`
  mutation MutationOrganization($input: OrganizationUpdateInput!) {
    organization {
      update(input: $input) {
        ... on OrganizationUpdateSuccess {
            result {
              id
            }
        }
      }
    }
  }
`);

const MutationSchedulingVariant = gql(`
  mutation MutationSchedulingVariant($schedulingVariantV1Input: SchedulingVariantV1UpdateInput!) {
    schedulingVariantV1 {
      update(input: $schedulingVariantV1Input) {
        ... on NotFoundError {
          message
        }
        ... on SchedulingVariantV1UpdateSuccess {
          result {
            id
          }
        }
      }
    }
  }
`);

interface OrganizationFormData {
  organization: Partial<Organization>;
  schedulingVariantV1: Partial<SchedulingVariantV1>;
  orgEventsMetadata: Partial<OrgEventsMetadata>;
}

export function MaggieTab() {
  const { organizationId } = useOrganizationContext();
  const [updates, setUpdates] = useState<Partial<OrganizationFormData>>({});
  const { toast } = useToast();
  const { isMaggieUser } = useOrganizationContext();

  const handleSaveSettings = async () => {
    if (updates.orgEventsMetadata) {
      await updateOrgEventMetadata({
        variables: {
          input: {
            where: {
              id: updates.orgEventsMetadata.id,
            },
            set: {
              significantDateSources:
                updates.orgEventsMetadata?.significantDateSources,
            },
          },
        },
        refetchQueries: ['OrgEventsMetadataQuery'],
      });
    }

    if (updates.organization) {
      await updateOrganization({
        variables: {
          input: {
            filters: { id: organizationId || '' },
            set: {
              postReview: updates.organization?.postReview,
            },
          },
        },
        refetchQueries: ['QueryGetOrganizationById'],
      });
    }

    if (updates.schedulingVariantV1) {
      await updateSchedulingVariant({
        variables: {
          schedulingVariantV1Input: {
            where: { id: updates.schedulingVariantV1.id || '' },
            set: {
              channels: updates.schedulingVariantV1.channels
                ? updates.schedulingVariantV1.channels.map(toChannelInput)
                : undefined,
              frequency: updates.schedulingVariantV1?.frequency,
            },
          },
        },
        refetchQueries: ['SocialMediaChannelsQuery'],
      });
    }

    setUpdates({});
  };

  const [updateOrgEventMetadata, { loading: updateOrgEventMetadataLoading }] =
    useMutation(MutationOrgEventMetadata, {
      onCompleted: (data) => {
        toast({
          description: 'Religious events succesfully saved',
          duration: 3000,
        });
      },
      onError: (error: ApolloError) => {
        toast({
          description: error.message,
          duration: 3000,
          variant: 'destructive',
        });
      },
    });

  const [updateSchedulingVariant, { loading: updateSchedulingVariantLoading }] =
    useMutation(MutationSchedulingVariant, {
      onCompleted: (data) => {
        toast({
          description: 'Social Media channels succesfully saved',
          duration: 3000,
        });
      },
      onError: (error: ApolloError) => {
        toast({
          description: error.message,
          duration: 3000,
          variant: 'destructive',
        });
      },
    });

  const [updateOrganization, { loading: updateOrganizationLoading }] =
    useMutation(MutationOrganization, {
      onCompleted: (data) => {
        toast({
          description: 'Customer review succesfully saved',
          duration: 3000,
        });
      },
      onError: (error: ApolloError) => {
        toast({
          description: error.message,
          duration: 3000,
          variant: 'destructive',
        });
      },
    });
  const loading =
    updateOrgEventMetadataLoading ||
    updateOrganizationLoading ||
    updateSchedulingVariantLoading;

  const handleUpdateReviews = (value: PostReview) => {
    setUpdates((prevState) => ({
      ...prevState,
      organization: { postReview: value },
    }));
  };

  const handleUpdateSocialMediaChannels = (
    channels?: SchedulingVariantV1Channel[],
    frequency?: number,
    id?: string
  ) => {
    if (frequency) {
      setUpdates((prevState) => ({
        ...prevState,
        schedulingVariantV1: {
          id,
          channels: updates.schedulingVariantV1?.channels,
          frequency: { weekly: frequency },
        },
      }));
    } else if (channels) {
      setUpdates((prevState) => ({
        ...prevState,
        schedulingVariantV1: {
          id,
          channels: channels,
          frequency: updates.schedulingVariantV1?.frequency,
        },
      }));
    }
  };

  const handleReligiousCalendarChanges = (
    id: string,
    keys: OrgEventsMetadataSignificantDateSource[]
  ) => {
    setUpdates((prevState) => ({
      ...prevState,
      orgEventsMetadata: { id, significantDateSources: keys },
    }));
  };

  if (!organizationId) {
    return (
      <div>
        <SettingsTabTitle title='Maggie' />
        <p>Please select an organization first.</p>
      </div>
    );
  }

  if (!isMaggieUser) {
    return (
      <div>
        <SettingsTabTitle title='Maggie' />
        <p>Please onboard Maggie first</p>
      </div>
    );
  }

  return (
    <div>
      <SettingsTabTitle title='Maggie' />
      <Suspense fallback={<Spinner />}>
        <SocialMediaChannels
          onChangeSchedulingVariant={handleUpdateSocialMediaChannels}
        />
      </Suspense>
      <Suspense fallback={<Spinner />}>
        <ReligiousCalendars onChange={handleReligiousCalendarChanges} />{' '}
      </Suspense>
      <Suspense fallback={<Spinner />}>
        <CustomerReviewSection onOrganizationChange={handleUpdateReviews} />
      </Suspense>
      <div className='mt-4 flex justify-end' onClick={handleSaveSettings}>
        <Button disabled={loading} variant={'maggie-primary'}>
          {loading ? <Spinner color='white' className='w-6' /> : 'Save'}
        </Button>
      </div>
    </div>
  );
}
